import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { Sparkles, Loader2, ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [propertyId, setPropertyId] = useState(() => localStorage.getItem("klena_last_pid") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!propertyId.trim()) {
      setError("Property ID is required. Ask your manager if you're not sure.");
      return;
    }
    setLoading(true);
    try {
      const user = await login(email.trim(), password, propertyId.trim());
      // Remember the Property ID for faster device logins — but NEVER for super admin.
      if (user.role === "super_admin") {
        localStorage.removeItem("klena_last_pid");
      } else {
        localStorage.setItem("klena_last_pid", propertyId.trim());
      }
      toast.success(`Welcome back, ${user.name}`);
      if (user.role === "cleaner") navigate("/clean");
      else if (user.role === "super_admin") navigate("/admin");
      else navigate("/manager");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col justify-center px-6 py-12 sm:px-16 lg:px-20">
        <div className="mx-auto w-full max-w-md k-fade-in">
          <div className="mb-10 flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary">
              <Sparkles className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <div className="font-display text-2xl font-extrabold leading-none">KlenaPro</div>
              <div className="label-caps text-muted-foreground">Housekeeping Ops</div>
            </div>
          </div>

          <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Sign in</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Manage housekeeping, timesheets and cleaner productivity.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@motel.co.nz"
                required
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="login-password-toggle"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground transition-colors hover:text-foreground"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="propertyId">Property ID</Label>
              <Input
                id="propertyId"
                data-testid="login-propertyid-input"
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value.toUpperCase())}
                placeholder="e.g. QN123456"
                autoComplete="off"
                required
              />
              <p className="text-xs text-muted-foreground">Your motel's Property ID (ask your manager).</p>
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm font-medium text-destructive">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="k-press h-11 w-full gap-2 text-base"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Sign in
            </Button>
          </form>

          <p className="mt-5 text-sm text-muted-foreground">
            New motel?{" "}
            <Link to="/signup" className="font-semibold text-foreground underline underline-offset-4" data-testid="to-signup-link">
              Start your 14-day free trial
            </Link>
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            <Link to="/terms" className="underline underline-offset-4" data-testid="to-terms-link">
              Terms &amp; Conditions
            </Link>
          </p>
        </div>
      </div>

      {/* Right: image panel */}
      <div className="relative hidden lg:block">
        <img
          src="https://customer-assets-7cd3h4nn.emergentagent.net/job_quantum-housekeep/artifacts/9jttffmn_image.png"
          alt="Motel"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-primary-foreground">
          <p className="font-display text-3xl font-bold leading-tight">
            Faster cleans.<br />Fairer timesheets.<br />Zero guesswork.
          </p>
          <p className="mt-4 max-w-md text-sm text-primary-foreground/80">
            Built to optimise and monitor motel cleaning — and adaptable to any task-oriented industry.
          </p>
        </div>
      </div>
    </div>
  );
}
